import type { ErrorLike } from '@apollo/client';

import { ApiErrorRules } from '@constants/apiErrors';
import {
  EnergyFuelType,
  EnergyMeterType,
  EnergyQuoteFragment,
  EnergyQuoteType,
} from '@generated/gql/graphql';
import { BooleanSelection } from '@src/types/index';
import { containsConstraintError } from '@utils/apiErrors';

const hasGasService = (fuelType: EnergyFuelType) =>
  fuelType !== EnergyFuelType.ElectricityOnly;

const hasElectricityService = (fuelType: EnergyFuelType) =>
  fuelType !== EnergyFuelType.GasOnly;

// If the meter type is econ7, it supports separate day and night usages
const hasEconomy7MeterType = (meterType: EnergyMeterType) =>
  meterType === EnergyMeterType.Economy_7;

interface MeterAnswers {
  fuelType: EnergyFuelType;
  hasPrepayment: BooleanSelection;
  prepaymentFuelType: EnergyFuelType;
}

// Which services are on a prepayment meter, derived from the form answers.
// With a single fuel the answer is implied, so the follow-up question is not asked.
const getEffectivePrepaymentFuelType = ({
  fuelType,
  hasPrepayment,
  prepaymentFuelType,
}: MeterAnswers): Nullable<EnergyFuelType> => {
  if (hasPrepayment !== BooleanSelection.Yes) {
    return null;
  }

  return fuelType === EnergyFuelType.DualFuel ? prepaymentFuelType : fuelType;
};

const hasElectricityPrepayment = (answers: MeterAnswers) => {
  const prepaymentFuel = getEffectivePrepaymentFuelType(answers);

  if (!prepaymentFuel) {
    return false;
  }

  return [EnergyFuelType.ElectricityOnly, EnergyFuelType.DualFuel].includes(
    prepaymentFuel
  );
};

// Economy 7 and prepayment are mutually exclusive for electricity
const isEconomy7QuestionVisible = (answers: MeterAnswers) =>
  hasElectricityService(answers.fuelType) && !hasElectricityPrepayment(answers);

const containsPrepaymentConstraint = (errors: ErrorLike[]) =>
  containsConstraintError(errors, ApiErrorRules.prepaymentMeter);

const getSnackbarErrors = (errors?: ErrorLike[]): ErrorLike[] => {
  // We don't want to show a snackbar error on specific API error responses as this is handled with modal
  if (!errors || containsPrepaymentConstraint(errors)) {
    return [];
  }

  return errors;
};

const getEnergyTariffByType = (
  tariffs: EnergyQuoteFragment[] | undefined,
  quoteType: EnergyQuoteType
): EnergyQuoteFragment | undefined => {
  return tariffs?.find((tariff) => tariff.quoteType === quoteType);
};

export {
  hasElectricityService,
  hasGasService,
  getSnackbarErrors,
  containsPrepaymentConstraint,
  hasEconomy7MeterType,
  hasElectricityPrepayment,
  isEconomy7QuestionVisible,
  getEffectivePrepaymentFuelType,
  getEnergyTariffByType,
};
